import React from 'react'
import { makeStyles, useBreakpoint, StatusDot, Tooltip, Typography, ListItem, ListItemText } from '@paintscout/ui'
import type { ListItemProps, MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import type { Quote } from '@paintscout/api'
import type { QuoteStatus } from 'paintscout'
import { formatName } from '@paintscout/util/builder'
import moment from 'moment'
import classNames from 'classnames'

const useStyles = makeStyles(
  (theme) => ({
    root: {
      height: '100%',
      maxHeight: '60vh',
      overflow: 'auto'
    },
    row: {},
    tooltip: {
      width: '100%',
      '& > *:first-child': {
        flex: 1
      }
    },
    htmlTooltip: {
      position: 'absolute',
      top: '-70px',
      left: '-85px',
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        top: '-65px'
      },
      [theme.breakpoints.down('xs')]: {
        top: '-75px'
      }
    }
  }),
  { name: 'QuoteResultsList' }
)

export interface QuoteResultsListProps {
  classes?: StyleClasses<typeof useStyles>
  disabled?: boolean
  results: Array<Partial<Quote>>
  disabledRow?: (quote: Partial<Quote>) => string
  onQuoteClick: (quote: Partial<Quote>) => any
}

function QuoteResultsList(props: QuoteResultsListProps) {
  const classes = useStyles(props)
  const { disabled, results, onQuoteClick, disabledRow } = props

  return (
    <div className={classes.root}>
      {results.map((quote, index) => {
        const disabledReason = disabledRow?.(quote)
        return (
          <QuoteListItem
            key={index}
            disabled={disabled || !!disabledReason}
            disabledReason={disabledReason}
            quote={quote}
            onClick={() => onQuoteClick(quote)}
          />
        )
      })}
    </div>
  )
}

/*******************************
 *         QuoteListItem     *
 *******************************/
const useListItemStyles = makeStyles((theme: Theme) => ({
  listItem: {
    maxWidth: '100%',
    overflowWrap: 'normal',
    display: 'flex',
    justifyContent: 'space-between',
    height: 74,
    [theme.breakpoints.down('sm')]: {
      height: 64
    }
  },
  disabled: {
    pointerEvents: 'none',
    backgroundColor: theme.paintscout.palette.brand.Neutral.P96
  },
  listItemText: {
    flex: 1
  },
  secondaryText: {
    color: theme.paintscout.palette.brand.Neutral.P70,
    fontStyle: 'italic',
    overflowWrap: 'normal',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '75%'
    }
  },
  estimator: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 320,
    [theme.breakpoints.down('sm')]: {
      maxWidth: 200
    },
    [theme.breakpoints.down(400)]: {
      maxWidth: 120
    }
  },
  email: {
    fontStyle: 'italic',
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      paddingRight: theme.spacing()
    }
  }
}))

function QuoteListItem({
  quote,
  disabledReason,
  ...props
}: {
  quote: Partial<Quote>
} & ListItemProps & { disabledReason?: string }) {
  const classes = useStyles()
  const listItemClasses = useListItemStyles({})
  const isSmallScreen = useBreakpoint((breakpoints) => breakpoints.down('sm'))

  const textBodySize = isSmallScreen ? 'body2' : 'body1'

  return (
    <Tooltip
      disabled={!disabledReason}
      hideIcon
      content={disabledReason}
      classes={{ badgeRoot: classes.tooltip, htmlTooltip: classes.htmlTooltip }}
    >
      <ListItem
        className={classNames({
          [listItemClasses.listItem]: true,
          [listItemClasses.disabled]: !!disabledReason
        })}
        divider
        button
        {...(props as any)}
      >
        <ListItemText
          classes={{ root: listItemClasses.listItemText, secondary: listItemClasses.secondaryText }}
          primary={
            <>
              <Typography variant={textBodySize} weight={500}>
                {formatName(quote.contact)}
              </Typography>
              <StatusDot dense={false} size={9} status={quote.status.value as QuoteStatus} archived={false}>
                {quote.status.value === 'onHold'
                  ? 'On Hold'
                  : quote.status.value.charAt(0).toUpperCase() + quote.status.value.slice(1)}
              </StatusDot>
            </>
          }
          secondary={''}
          primaryTypographyProps={{
            variant: textBodySize
          }}
          secondaryTypographyProps={{
            variant: textBodySize
          }}
        />

        <div>
          <Typography variant={textBodySize} align="right">
            #{quote.number} - {moment(quote.quote_date).format('MM/DD/YY')}
          </Typography>
          <Typography variant={textBodySize} align="right" className={listItemClasses.estimator}>
            {formatName(quote.owner)}
          </Typography>
        </div>
      </ListItem>
    </Tooltip>
  )
}

export default QuoteResultsList
